'use client';

import { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  alpha,
  IconButton,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Bookmark as BookmarkIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import AddressAutocomplete from '@/components/AddressAutocomplete/AddressAutocomplete';
import { buildApiConfig } from '@/services/api/apiClient';
import { endPointEnums } from '@/services/api/endpoints';
import useApiCall from '@/services/api/useApiCall';
import { useTranslation } from '@/kit/useTranslation';
import { lookupPincode } from '@/utils/pincodeLookup';

const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
  success: '#10b981',
};

export default function DeliveryAddressAccordion({ item, onSave, onCancel }) {
  const { t } = useTranslation();
  const { makeApiCall } = useApiCall();

  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);

  const [selectedAddress, setSelectedAddress] = useState(null);
  // Initialize addressInput correctly for both old and new formats
  const initialAddressText =
    typeof item.deliveryAddress === 'string'
      ? item.deliveryAddress
      : item.deliveryAddress?.address || item.deliveryAddress?.formatted_address || '';
  const [addressInput, setAddressInput] = useState(initialAddressText);
  useEffect(() => {
    setAddressInput(selectedAddress?.address || selectedAddress?.formatted_address || '');
  }, [selectedAddress]);
  const [recipientName, setRecipientName] = useState(item.recipientName || '');
  const [recipientPhone, setRecipientPhone] = useState(item.recipientPhone || '');
  const [saveAddress, setSaveAddress] = useState(false);

  const [city, setCity] = useState('');
  const [stateName, setStateName] = useState('');
  const [pincode, setPincode] = useState('');

  // Auto-populate city & state when a valid 6-digit pincode is entered
  useEffect(() => {
    if (!pincode || pincode.length !== 6) return;
    let cancelled = false;
    lookupPincode(pincode).then((result) => {
      if (cancelled || !result) return;
      if (!city) setCity(result.city);
      if (!stateName) setStateName(result.state);
    });
    return () => {
      cancelled = true;
    };
  }, [pincode]); // intentionally omit city/stateName to avoid overwriting user edits

  const handlePlaceSelected = useCallback((place) => {
    if (!place) {
      setSelectedAddress(null);
      setAddressInput('');
      setCity('');
      setStateName('');
      setPincode('');
      return;
    }
    const formatted = place.fullAddress || place.formatted_address || '';

    // Extract components
    const c = place.city || place.city || '';
    const s = place.state || place.administrative_area_level_1 || '';
    const p = place.pincode || place.postal_code || '';

    // Store full address object matching individual parcel format
    setSelectedAddress({
      type: 'recent',
      address: formatted,
      formatted_address: formatted,
      latitude: place.latitude || place.lat,
      longitude: place.longitude || place.lng,
      city: c,
      state: s,
      pincode: p,
    });
    setAddressInput(formatted);
    setCity(c);
    setStateName(s);
    setPincode(p);
  }, []);

  useEffect(() => {
    const fetchSavedAddresses = async () => {
      try {
        const config = buildApiConfig(endPointEnums.GET_SAVED_ADDRESSES);
        const response = await makeApiCall(config);
        if (response?.success) {
          // Filter for DELIVERY or BOTH types - handle both prefixed and unprefixed field names
          const deliveryAddresses = (response.data || []).filter((addr) => {
            const type = addr.savedAddressType || addr.addressType || '';
            return type === 'DELIVERY' || type === 'BOTH';
          });
          setSavedAddresses(deliveryAddresses);
        }
      } catch (err) {
        console.error('[DeliveryAddressAccordion] Failed to fetch saved addresses:', err);
      } finally {
        setLoadingSaved(false);
      }
    };
    fetchSavedAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Initialize from existing item data (handle both old and new formats)
    if (item.deliveryAddress) {
      if (typeof item.deliveryAddress === 'object' && item.deliveryAddress.latitude) {
        // New format: full address object
        setSelectedAddress(item.deliveryAddress);
      } else if (item.deliveryLat && item.deliveryLng) {
        // Old format: separate fields
        setSelectedAddress({
          type: 'recent',
          address:
            typeof item.deliveryAddress === 'string'
              ? item.deliveryAddress
              : item.deliveryAddress?.address || '',
          formatted_address:
            typeof item.deliveryAddress === 'string'
              ? item.deliveryAddress
              : item.deliveryAddress?.formatted_address || '',
          latitude: item.deliveryLat,
          longitude: item.deliveryLng,
        });
      }
    }
  }, [item.deliveryAddress, item.deliveryLat, item.deliveryLng]);

  const handleSavedAddressSelect = useCallback((addr) => {
    // Normalize fields - API returns both prefixed (savedAddress*) and unprefixed names
    const normalizedAddr = {
      type: 'saved',
      address:
        addr.savedAddressFullAddress ||
        addr.fullAddress ||
        addr.savedAddressLabel ||
        addr.addressLabel ||
        addr.address ||
        '',
      formatted_address: addr.savedAddressFullAddress || addr.fullAddress || addr.address || '',
      latitude: addr.savedAddressLatitude || addr.latitude,
      longitude: addr.savedAddressLongitude || addr.longitude,
      city: addr.savedAddressCity || addr.city || '',
      state: addr.savedAddressState || addr.state || '',
      pincode: addr.savedAddressPincode || addr.pincode || '',
    };
    setSelectedAddress(normalizedAddr);

    // Set recipient details from saved address
    const contactName = addr.savedAddressContactPersonName || addr.contactPersonName;
    const contactPhone = addr.savedAddressContactPersonPhone || addr.contactPersonPhone;
    if (contactName) setRecipientName(contactName);
    if (contactPhone) setRecipientPhone(contactPhone);
  }, []);

  const handleClear = useCallback(() => {
    setSelectedAddress(null);
    setAddressInput('');
    setCity('');
    setStateName('');
    setPincode('');
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedAddress || !selectedAddress.latitude) {
      alert(t('bulkBooking.selectValidAddress'));
      return;
    }
    // Return full address object matching individual parcel format
    onSave({
      addressObject: {
        type: selectedAddress.type || 'recent',
        address: selectedAddress.address,
        formatted_address: selectedAddress.formatted_address || selectedAddress.address,
        latitude: selectedAddress.latitude,
        longitude: selectedAddress.longitude,
        city: city || selectedAddress.city,
        state: stateName || selectedAddress.state,
        pincode: pincode || selectedAddress.pincode,
      },
      recipientName,
      recipientPhone,
      saveDeliveryAddress: saveAddress,
    });
  }, [
    selectedAddress,
    recipientName,
    recipientPhone,
    saveAddress,
    onSave,
    city,
    stateName,
    pincode,
  ]);

  return (
    <Stack direction="row" spacing={2} alignItems="flex-start">
      {/* Main Form - Left Side */}
      <Box sx={{ flex: 1 }}>
        {/* Modern Header */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box
              sx={{
                bgcolor: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                p: 0.75,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'left',
                boxShadow: '0 2px 8px rgba(245,158,11,0.25)',
              }}
            >
              <LocationIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  fontSize: '0.95rem',
                  lineHeight: 1.2,
                }}
              >
                {t('bulkBooking.deliveryDetails')}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 500 }}>
                {t('bulkBooking.addCompleteDeliveryInfo')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onCancel}
            sx={{
              bgcolor: alpha('#ef4444', 0.08),
              '&:hover': { bgcolor: alpha('#ef4444', 0.15) },
              transition: 'all 200ms',
            }}
            aria-label="close"
          >
            <CloseIcon sx={{ fontSize: 16, color: '#ef4444' }} />
          </IconButton>
        </Stack>

        {/* Main Form Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2.5,
            bgcolor: '#fff',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          {/* Address Search Section */}
          <Box sx={{ mb: 2.5 }}>
            {/* Small Clear control for clearing the inputs */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                variant="text"
                onClick={handleClear}
                sx={{ textTransform: 'none', color: '#6b7280' }}
              >
                {t('bulkBooking.clear')}
              </Button>
            </Box>{' '}
            {/* Inline Saved Addresses (mobile-first) */}
            {!loadingSaved && savedAddresses && savedAddresses.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 3,
                      height: 16,
                      bgcolor: '#10b981',
                      borderRadius: 0.5,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#10b981',
                      fontWeight: 800,
                      letterSpacing: 0.2,
                    }}
                  >
                    Saved Addresses
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                  {savedAddresses.slice(0, 8).map((addr) => {
                    // Normalize display label - handle both prefixed and unprefixed field names
                    const displayLabel =
                      addr.savedAddressLabel ||
                      addr.addressLabel ||
                      addr.savedAddressFullAddress ||
                      addr.fullAddress ||
                      addr.address ||
                      'Address';
                    const truncated =
                      displayLabel.length > 30
                        ? displayLabel.substring(0, 30) + '...'
                        : displayLabel;
                    return (
                      <Button
                        key={addr.savedAddressId || addr.addressId}
                        variant="outlined"
                        size="small"
                        onClick={() => handleSavedAddressSelect(addr)}
                        sx={{
                          whiteSpace: 'nowrap',
                          minWidth: 'auto',
                          borderColor: '#e5e7eb',
                          color: '#374151',
                          '&:hover': {
                            borderColor: '#10b981',
                            bgcolor: alpha('#10b981', 0.04),
                          },
                        }}
                        title={displayLabel}
                      >
                        {truncated}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 3,
                  height: 16,
                  bgcolor: '#f59e0b',
                  borderRadius: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#111827',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                }}
              >
                {t('bulkBooking.addressSearch')}
              </Typography>
            </Stack>
            <AddressAutocomplete
              value={addressInput || addressInput.address || ''}
              onChange={(v) => setAddressInput(v)}
              onPlaceSelected={handlePlaceSelected}
              label=""
              placeholder={t('bulkBooking.typeToSearchAddress')}
              required
              clearable
              size="medium"
              fullWidth
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.75rem',
                  padding: '12px 14px',
                },
                '& .MuiInputLabel-root': { fontSize: '0.85rem' },
                '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
              }}
            />
          </Box>

          {/* Location Details Grid */}
          <Box sx={{ mb: 2.5 }}>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 3,
                  height: 16,
                  bgcolor: '#10b981',
                  borderRadius: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#111827',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                }}
              >
                {t('bulkBooking.locationLabel')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                label={t('bulkBooking.pin')}
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                size="small"
                sx={{
                  width: 100,
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '1rem',
                    fontWeight: 700,
                    py: '10px',
                  },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f59e0b',
                      borderWidth: 1.5,
                    },
                  },
                }}
              />
              <TextField
                label={t('bulkBooking.city')}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  },
                  '& .MuiInputBase-input': { fontSize: '1rem' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f59e0b',
                      borderWidth: 1.5,
                    },
                  },
                }}
              />
              <TextField
                label={t('bulkBooking.state')}
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiInputLabel-root': {
                    fontSize: '0.85rem',
                    fontWeight: 700,
                  },
                  '& .MuiInputBase-input': { fontSize: '1rem' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f59e0b',
                      borderWidth: 1.5,
                    },
                  },
                }}
              />
            </Stack>
          </Box>

          {/* Recipient Details Grid */}
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 3,
                  height: 16,
                  bgcolor: '#3b82f6',
                  borderRadius: 0.5,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#111827',
                  fontWeight: 700,
                  letterSpacing: 0.2,
                }}
              >
                {t('bulkBooking.recipientLabel')}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  '& .MuiOutlinedInput-root': {
                    pl: 0.5,
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f59e0b',
                      borderWidth: 1.5,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    bgcolor: alpha('#3b82f6', 0.1),
                    p: 0.5,
                    borderRadius: 1,
                    display: 'flex',
                  }}
                >
                  <PersonIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                </Box>
                <TextField
                  label={t('bulkBooking.fullName')}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      ml: 4,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                      pl: 5,
                      py: '9px',
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                  '& .MuiOutlinedInput-root': {
                    pl: 0.5,
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#f59e0b' },
                    '&.Mui-focused fieldset': {
                      borderColor: '#f59e0b',
                      borderWidth: 1.5,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1,
                    bgcolor: alpha('#10b981', 0.1),
                    p: 0.5,
                    borderRadius: 1,
                    display: 'flex',
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 16, color: '#10b981' }} />
                </Box>
                <TextField
                  label={t('bulkBooking.phone')}
                  value={recipientPhone}
                  type="tel"
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      ml: 4,
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.85rem',
                      pl: 5,
                      py: '9px',
                    },
                  }}
                />
              </Box>
            </Stack>
          </Box>

          {/* Save Checkbox */}
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              mb: 2,
              borderRadius: 1.5,
              bgcolor: alpha('#f59e0b', 0.04),
              border: '1px dashed',
              borderColor: alpha('#f59e0b', 0.2),
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveAddress}
                  onChange={(e) => setSaveAddress(e.target.checked)}
                  size="small"
                  sx={{
                    '&.Mui-checked': { color: '#f59e0b' },
                    '& .MuiSvgIcon-root': { fontSize: 20 },
                  }}
                />
              }
              label={
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#111827',
                      fontWeight: 700,
                    }}
                  >
                    {t('bulkBooking.saveForFutureUse')}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.68rem',
                      color: '#94a3b8',
                      fontWeight: 500,
                    }}
                  >
                    {t('bulkBooking.quickAccessNextBooking')}
                  </Typography>
                </Box>
              }
            />
          </Paper>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onCancel}
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 700,
                borderColor: '#e5e7eb',
                color: '#6b7280',
                px: 2.5,
                py: 0.75,
                borderRadius: 1.5,
                '&:hover': {
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  bgcolor: alpha('#ef4444', 0.05),
                },
              }}
            >
              {t('bulkBooking.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
              disabled={!selectedAddress || !selectedAddress.latitude}
              sx={{
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
                px: 3,
                py: 0.75,
                borderRadius: 1.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                  boxShadow: '0 4px 16px rgba(245,158,11,0.4)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#94a3b8',
                  boxShadow: 'none',
                },
                transition: 'all 200ms',
              }}
            >
              {t('bulkBooking.saveAndContinue')}
            </Button>
          </Stack>
        </Paper>
      </Box>

      {/* Saved Addresses - Right Panel (hidden on small screens) */}
      <Box sx={{ width: 380, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
        {!loadingSaved ? (
          savedAddresses.length > 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: '#fafafa',
                border: '1px solid #e5e7eb',
                position: 'sticky',
                top: 20,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
                <BookmarkIcon sx={{ fontSize: 16, color: '#10b981' }} />
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: '#10b981',
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    textTransform: 'uppercase',
                  }}
                >
                  {t('bulkBooking.savedAddresses')}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {savedAddresses.slice(0, 8).map((addr) => {
                  // Normalize field names - API returns both prefixed and unprefixed versions
                  const displayLabel =
                    addr.savedAddressLabel || addr.addressLabel || addr.label || '';
                  const displayAddress =
                    addr.savedAddressFullAddress || addr.fullAddress || addr.address || '';
                  return (
                    <Paper
                      key={addr.savedAddressId || addr.addressId}
                      elevation={0}
                      onClick={() => handleSavedAddressSelect(addr)}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: '#fff',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 200ms',
                        '&:hover': {
                          borderColor: '#10b981',
                          bgcolor: alpha('#10b981', 0.05),
                          transform: 'translateX(4px)',
                          boxShadow: '0 2px 8px rgba(16,185,129,0.15)',
                        },
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" spacing={1}>
                        <Box
                          sx={{
                            bgcolor: alpha('#10b981', 0.1),
                            p: 0.5,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <LocationIcon sx={{ fontSize: 14, color: '#10b981' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {displayLabel && (
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#111827',
                                mb: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {displayLabel}
                            </Typography>
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.68rem',
                              color: '#64748b',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {displayAddress}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: '1px dashed #cbd5e1',
                bgcolor: '#fafafa',
                textAlign: 'center',
              }}
            >
              <BookmarkIcon sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
                {t('bulkBooking.noSavedAddresses')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: '#94a3b8',
                  mt: 0.5,
                  lineHeight: 1.4,
                }}
              >
                {t('bulkBooking.saveAddressesFasterBooking')}
              </Typography>
            </Paper>
          )
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px dashed #e5e7eb',
              bgcolor: '#fafafa',
              textAlign: 'center',
            }}
          >
            <CircularProgress size={24} sx={{ color: '#f59e0b', mb: 1 }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              {t('bulkBooking.loading')}
            </Typography>
          </Paper>
        )}
      </Box>
    </Stack>
  );
}
